import { useEffect, useMemo, useRef, useState } from 'react';
import type { Draft } from 'immer';
import type { LayerRole, LevelDoc, TileLayer } from '../model/format.ts';
import { gameData } from '../data/gameData.ts';
import { instantiatePrefab } from '../data/prefabInstance.ts';
import { terrainStyles } from '../terrain/index.ts';
import { activeLayerOf, draftContainer, getContainer, useEditor } from './store.ts';
import { newLayer, flipFlags, rotateFlags, setModIn } from './actions.ts';
import { FieldEditor } from './FieldEditor.tsx';
import { entityNamer } from './links.ts';
import { useSettings } from './settings.ts';
import { paletteGroups, prefabLabel } from './catalog.ts';
import { behaviorLabel } from './bodies.ts';
import { cameraAddr, cameraBounds, cameraView, fitCameraBounds, writeCameraBounds, type CameraBounds } from './cameraBounds.ts';
import { addPlayerOverrides, objectComps, playerOverridesComp } from './components.ts';
import { CompFields } from './SimpleFields.tsx';
import { Check, ColorInput, Icon, NumberInput, PaletteThumb, PrefabThumb, Row, Section, Select, TextInput } from './widgets.tsx';

// ---------- Layers ----------

const ROLE_OPTIONS: [LayerRole, string][] = [['level', 'Solid ground'], ['foreground', 'Foreground'], ['background', 'Background'], ['decor', 'Decoration'], ['oneway', 'One-way platforms'], ['spikes', 'Spikes']];
const roleLabel = (r: LayerRole) => ROLE_OPTIONS.find(([k]) => k === r)?.[1] ?? r;

export function LayersPanel() {
  const s = useEditor();
  const doc = s.doc!;
  const cont = getContainer(doc, s.container);
  const active = activeLayerOf(s);
  const advanced = useSettings((x) => x.advanced);
  const gd = gameData();
  const sorted = [...cont.layers].sort((a, b) => (gd.sortingIndex.get(b.sortingLayer) ?? 0) - (gd.sortingIndex.get(a.sortingLayer) ?? 0) || b.sortingOrder - a.sortingOrder);
  const updLayer = (id: string, label: string, fn: (l: Draft<TileLayer>) => void) =>
    s.change(label, (d) => {
      const l = draftContainer(d, s.container).layers.find((x) => x.id === id);
      if (l) fn(l);
    });
  const addLayer = (role: LayerRole) => {
    const l = newLayer(role, s.container !== 'level', role === 'decor' ? 'Decoration' : undefined);
    s.change('Add layer', (d) => { draftContainer(d, s.container).layers.push(l); });
    s.set({ activeLayer: { ...s.activeLayer, [s.container]: l.id } });
  };
  const removeLayer = (id: string) => {
    if (cont.terrains.some((t) => t.targets.level === id)) {
      s.notify('A terrain shape outputs ground to this layer; retarget it first');
      return;
    }
    s.change('Delete layer', (d) => {
      const c = draftContainer(d, s.container);
      c.layers = c.layers.filter((l) => l.id !== id);
      for (const t of c.terrains) {
        if (t.targets.foreground === id) delete t.targets.foreground;
        if (t.targets.background === id) delete t.targets.background;
      }
    });
  };
  return (
    <div className="panel-scroll">
      {s.container !== 'level' && (
        <div className="banner">
          Editing body <b>{cont.body?.name}</b>
          <button className="btn" onClick={() => s.set({ container: 'level' })}>Done</button>
        </div>
      )}
      <Section
        title="Tile layers"
        right={
          <Select<string>
            value=""
            options={[['', '+ Add'], ...ROLE_OPTIONS.filter(([k]) => k !== 'spikes')]}
            onChange={(v) => v && addLayer(v as LayerRole)}
          />
        }
      >
        {sorted.map((l) => (
          <div key={l.id} className={`layer-row ${active?.id === l.id ? 'active' : ''}`} onClick={() => s.set({ activeLayer: { ...s.activeLayer, [s.container]: l.id } })}>
            <button className="icon-btn" title="Visible" onClick={(e) => { e.stopPropagation(); updLayer(l.id, 'Toggle layer', (x) => { x.visible = x.visible === false ? undefined : false; }); }}>
              <Icon name={l.visible === false ? 'eyeoff' : 'eye'} size={16} />
            </button>
            <button className="icon-btn" title="Locked" onClick={(e) => { e.stopPropagation(); updLayer(l.id, 'Lock layer', (x) => { x.locked = !x.locked || undefined; }); }}>
              <Icon name={l.locked ? 'lock' : 'unlock'} size={16} />
            </button>
            <span className="layer-name">{l.name}</span>
            <span className="layer-meta">{advanced ? `${l.role} · ${l.sortingLayer}${l.sortingOrder ? ` ${l.sortingOrder}` : ''}` : roleLabel(l.role)}</span>
          </div>
        ))}
      </Section>
      {active && (
        <Section title={`Layer: ${active.name}`}>
          <Row label="Name"><TextInput value={active.name} onChange={(v) => updLayer(active.id, 'Rename layer', (x) => { x.name = v; })} /></Row>
          <Row label="Used for">
            <Select<LayerRole> value={active.role} options={ROLE_OPTIONS} onChange={(v) => updLayer(active.id, 'Layer role', (x) => { x.role = v; })} />
          </Row>
          {advanced && (
            <>
              <Row label="Sorting layer">
                <Select value={active.sortingLayer} options={gd.project.sortingLayers.map((n) => [n, n] as [string, string])} onChange={(v) => updLayer(active.id, 'Sorting layer', (x) => { x.sortingLayer = v; })} />
              </Row>
              <Row label="Order in layer"><NumberInput value={active.sortingOrder} onChange={(v) => updLayer(active.id, 'Sorting order', (x) => { x.sortingOrder = v; })} /></Row>
            </>
          )}
          <Row label="Tint"><ColorInput value={(active.color ?? [1, 1, 1, 1]) as [number, number, number, number]} onChange={(c) => updLayer(active.id, 'Tint', (x) => { x.color = c.every((v) => v === 1) ? undefined : c; })} /></Row>
          <Row label="Tiles hurt" title="Spike colliders (SpikeTilemapGeneratorFlag)"><Check value={!!active.spikeGenerator} onChange={(v) => updLayer(active.id, 'Spike flag', (x) => { x.spikeGenerator = v || undefined; })} /></Row>
          <Row label="Jump through" title="One-way colliders (OneWayColliderGeneratorFlag)"><Check value={!!active.oneWayGenerator} onChange={(v) => updLayer(active.id, 'One-way flag', (x) => { x.oneWayGenerator = v || undefined; })} /></Row>
          <Row label="Tiles"><span className="muted">{active.tiles.length / 4} hand-placed</span></Row>
          <div className="insp-actions">
            <button className="btn danger" onClick={() => removeLayer(active.id)}>Delete layer</button>
          </div>
        </Section>
      )}
      <Section title={`Terrain shapes (${cont.terrains.length})`} open={cont.terrains.length < 30}>
        {cont.terrains.length === 0 && <div className="muted small">Use the polygon tool (P) to draw terrain.</div>}
        {cont.terrains.map((t, i) => (
          <div key={t.id} className={`layer-row ${s.selection.kind === 'terrain' && s.selection.id === t.id ? 'active' : ''}`} onClick={() => s.set({ selection: { kind: 'terrain', id: t.id } })}>
            <span className="layer-name">{t.name ?? `${t.mode === 'add' ? 'Shape' : 'Cut'} ${i + 1}`}</span>
            <span className="layer-meta">{terrainStyles().find((x) => x.id === t.style)?.name ?? t.style}</span>
          </div>
        ))}
      </Section>
      {s.container === 'level' && (
        <Section title={`Moving platforms & blocks (${doc.bodies.length})`} open={false}>
          {doc.bodies.length === 0 && <div className="muted small">Right-click (or long-press) a shape and choose “Make moving platform”.</div>}
          {doc.bodies.map((b) => (
            <div key={b.id} className={`layer-row ${s.selection.kind === 'body' && s.selection.id === b.id ? 'active' : ''}`} onClick={() => s.set({ selection: { kind: 'body', id: b.id }, panel: 'inspector' })}>
              <span className="layer-name">{b.name}</span>
              <span className="layer-meta">{behaviorLabel(b.behavior)}</span>
              <button className="icon-btn" title="Edit tiles" onClick={(e) => { e.stopPropagation(); s.set({ container: b.id, selection: { kind: 'body', id: b.id } }); }}>
                <Icon name="enter" size={16} />
              </button>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

// ---------- Tiles ----------

export function TilesPanel() {
  const s = useEditor();
  const doc = s.doc!;
  const gd = gameData();
  const tilesets = useMemo(
    () => Object.entries(gd.sprites.textures).filter(([, t]) => t.sprites.length >= 6 && t.ppu === 16).sort((a, b) => a[1].path.localeCompare(b[1].path)),
    [gd],
  );
  const usedTex = useMemo(() => {
    const c = new Map<string, number>();
    for (const p of doc.palette) c.set(p.tex, (c.get(p.tex) ?? 0) + 1);
    return c;
  }, [doc.palette]);
  const [source, setSource] = useState<string>(() => [...usedTex.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'palette');
  const setBrush = (patch: Partial<typeof s.brush>) => s.set({ brush: { ...s.brush, ...patch } });
  useEffect(() => {
    const on = (e: Event) => {
      const st = useEditor.getState();
      const d = (e as CustomEvent).detail;
      const f = d === 'flipx' ? flipFlags(st.brush.flags, 'x') : d === 'flipy' ? flipFlags(st.brush.flags, 'y') : rotateFlags(st.brush.flags);
      st.set({ brush: { ...st.brush, flags: f } });
    };
    window.addEventListener('ple:brush', on);
    return () => window.removeEventListener('ple:brush', on);
  }, []);
  return (
    <div className="panel-scroll">
      <div className="brush-bar">
        <PaletteThumb entry={s.brush.entry} size={40} flags={s.brush.flags} />
        <button className="icon-btn" title="Flip X (X)" onClick={() => setBrush({ flags: flipFlags(s.brush.flags, 'x') })}><Icon name="flipx" /></button>
        <button className="icon-btn" title="Flip Y (Y)" onClick={() => setBrush({ flags: flipFlags(s.brush.flags, 'y') })}><Icon name="flipy" /></button>
        <button className="icon-btn" title="Rotate (Z)" onClick={() => setBrush({ flags: rotateFlags(s.brush.flags) })}><Icon name="rotate" /></button>
        <span className="inline small">
          Size
          <Select value={s.brush.size} options={[1, 2, 3, 4, 6, 8].map((n) => [n, String(n)] as [number, string])} onChange={(v) => setBrush({ size: v })} />
        </span>
      </div>
      <div className="row">
        <span className="row-label">Source</span>
        <Select
          value={source}
          options={[['palette', `This level's tiles (${doc.palette.length})`], ...tilesets.map(([g, t]) => [g, `${t.path.split('/').pop()}${usedTex.has(g) ? ' •' : ''}`] as [string, string])]}
          onChange={setSource}
        />
      </div>
      {source === 'palette' ? (
        <div className="tile-grid">
          {doc.palette.map((p, i) => (
            <button
              key={i}
              className={`tile-btn ${s.brush.entry === p ? 'active' : ''}`}
              title={`${p.sprite}`}
              onClick={() => s.set({ brush: { ...s.brush, entry: p, flags: 0 }, tool: s.tool === 'brush' || s.tool === 'rect' ? s.tool : 'brush' })}
            >
              <PaletteThumb entry={p} size={28} />
            </button>
          ))}
        </div>
      ) : (
        <TextureSheet tex={source} />
      )}
    </div>
  );
}

function TextureSheet({ tex }: { tex: string }) {
  const s = useEditor();
  const gd = gameData();
  const t = gd.sprites.textures[tex];
  const ref = useRef<HTMLCanvasElement>(null);
  const [scale, setScale] = useState(2);
  useEffect(() => {
    const c = ref.current;
    const img = gd.images.get(tex);
    if (!c || !img || !t) return;
    c.width = t.w * scale;
    c.height = t.h * scale;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.fillStyle = '#0d0d12';
    g.fillRect(0, 0, c.width, c.height);
    g.drawImage(img, 0, 0, c.width, c.height);
    g.strokeStyle = 'rgba(255,255,255,0.08)';
    for (const sp of t.sprites) g.strokeRect(sp.r[0] * scale + 0.5, (t.h - sp.r[1] - sp.r[3]) * scale + 0.5, sp.r[2] * scale - 1, sp.r[3] * scale - 1);
    const cur = s.brush.entry;
    if (cur && cur.tex === tex) {
      const sp = t.sprites.find((x) => x.n === cur.sprite);
      if (sp) {
        g.strokeStyle = '#ffe066';
        g.lineWidth = 2;
        g.strokeRect(sp.r[0] * scale, (t.h - sp.r[1] - sp.r[3]) * scale, sp.r[2] * scale, sp.r[3] * scale);
      }
    }
  }, [tex, scale, s.brush.entry, gd, t]);
  if (!t) return null;
  const onClick = (e: React.MouseEvent) => {
    const r = ref.current!.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * t.w;
    const py = t.h - ((e.clientY - r.top) / r.height) * t.h;
    const sp = t.sprites.find((x) => px >= x.r[0] && px < x.r[0] + x.r[2] && py >= x.r[1] && py < x.r[1] + x.r[3]);
    if (!sp) return;
    const tileGuid = Object.entries(gd.sprites.tiles).find(([, ta]) => ta.sprite?.[0] === tex && ta.sprite?.[1] === sp.n)?.[0];
    s.set({ brush: { ...s.brush, entry: tileGuid ? { tex, sprite: sp.n, tile: tileGuid } : { tex, sprite: sp.n }, flags: 0 }, tool: s.tool === 'brush' || s.tool === 'rect' ? s.tool : 'brush' });
  };
  return (
    <div>
      <div className="inline small pad">
        Zoom
        <Select value={scale} options={[1, 2, 3, 4].map((n) => [n, `${n}×`] as [number, string])} onChange={setScale} />
        <span className="muted">{t.path}</span>
      </div>
      <div className="sheet-wrap">
        <canvas ref={ref} className="sheet" onClick={onClick} style={{ width: t.w * scale, height: t.h * scale }} />
      </div>
    </div>
  );
}

// ---------- Objects ----------

export function ObjectsPanel() {
  const s = useEditor();
  const gd = gameData();
  const [q, setQ] = useState('');
  const allPrefabs = useSettings((x) => x.allPrefabs);
  const groups = useMemo(() => paletteGroups(allPrefabs, q), [allPrefabs, q, gd]);
  return (
    <div className="panel-scroll">
      <div className="pad">
        <input className="text search" placeholder="Search objects…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {groups.map(([g, guids]) => (
        <Section key={g} title={g} open={g !== 'Internal'}>
          <div className="obj-grid">
            {guids.map((guid) => (
              <button
                key={guid}
                className={`obj-btn ${s.objectPrefab === guid ? 'active' : ''}`}
                onClick={() => s.set({ objectPrefab: guid, tool: 'object' })}
                title={gd.prefabs[guid].path}
              >
                <PrefabThumb guid={guid} size={44} />
                <span>{prefabLabel(guid)}</span>
              </button>
            ))}
          </div>
        </Section>
      ))}
    </div>
  );
}

// ---------- Level settings ----------

function CameraBoundsSection() {
  const s = useEditor();
  const doc = s.doc!;
  const addr = cameraAddr(doc);
  const b = cameraBounds(doc);
  const shown = useSettings((x) => x.showCameraBounds);
  if (!addr || !b) {
    return (
      <Section title="Camera bounds">
        <div className="muted small">This level has no camera object, so the camera follows the player everywhere.</div>
      </Section>
    );
  }
  const set = (p: Partial<CameraBounds>) => s.change('Camera bounds', (d) => writeCameraBounds(d, addr, p));
  const [vw, vh] = cameraView(doc);
  const editInView = () => {
    const r = document.querySelector('.viewport')?.getBoundingClientRect();
    const zoom = r ? Math.max(4, Math.min(48, 0.8 * Math.min(r.width / b.w, r.height / b.h))) : s.camera.zoom;
    if (!shown) useSettings.getState().update({ showCameraBounds: true });
    s.set({
      selection: { kind: 'objects', ids: [doc.system.camera!] },
      tool: 'select',
      container: 'level',
      camera: { zoom, x: b.x + b.w / 2, y: b.y + b.h / 2 },
    });
  };
  const fit = () => {
    const f = fitCameraBounds(doc);
    if (f) set({ ...f, enabled: true });
  };
  return (
    <Section title="Camera bounds">
      <Row label="Keep camera inside" title="The camera stops scrolling at these edges (off: it follows the player anywhere)">
        <Check value={b.enabled} onChange={(v) => set({ enabled: v })} />
      </Row>
      <Row label="Bottom left">
        <span className="vec">
          <NumberInput value={b.x} width={70} onChange={(v) => set({ x: v })} />
          <NumberInput value={b.y} width={70} onChange={(v) => set({ y: v })} />
        </span>
      </Row>
      <Row label="Size">
        <span className="vec">
          <NumberInput value={b.w} width={70} min={1} onChange={(v) => set({ w: v })} />
          <NumberInput value={b.h} width={70} min={1} onChange={(v) => set({ h: v })} />
        </span>
      </Row>
      {b.enabled && (b.w < vw || b.h < vh) && (
        <div className="row small warn">Smaller than the camera view ({Math.round(vw)} × {Math.round(vh)} tiles at 16:9): the view sticks to the left / bottom edge there.</div>
      )}
      <div className="insp-actions">
        <button className="btn" onClick={editInView}><Icon name="target" size={16} /> Edit in view</button>
        <button className="btn" onClick={fit} title="Bounds around all terrain, tiles and objects, with 2 tiles of margin">Fit to level</button>
      </div>
    </Section>
  );
}

function PlayerSection() {
  const s = useEditor();
  const doc = s.doc!;
  const comp = playerOverridesComp(doc);
  return (
    <Section title="Player">
      <div className="muted small">Check a setting to override the player prefab for this level. Unchecked settings stay at the default shown.</div>
      {comp ? (
        <CompFields doc={doc} comp={comp} />
      ) : (
        <div className="insp-actions">
          <button className="btn" onClick={() => s.change('Add player settings', (d) => { addPlayerOverrides(d); })}>Customize this level</button>
        </div>
      )}
    </Section>
  );
}

/** LevelData on the level-scripts object: the string that ties multiplayer players together. */
function PlayerStringSection({ doc }: { doc: LevelDoc }) {
  const id = doc.system.levelScripts;
  const o = id ? doc.objects.find((x) => x.id === id) : undefined;
  const comp = o ? objectComps(doc, o).find((c) => c.type === 'LevelData') : undefined;
  return (
    <Section title="Multiplayer string">
      {comp ? <CompFields doc={doc} comp={comp} /> : <div className="muted small">This level has no level scripts, so the string cannot be changed.</div>}
    </Section>
  );
}

export function LevelPanel() {
  const s = useEditor();
  const doc = s.doc!;
  const gd = gameData();
  const parallaxOptions: [string, string][] = [['', 'None'], ...(gd.project.prefabGroups['Parallax'] ?? []).map((g) => [g, gd.prefabs[g]?.name ?? g] as [string, string])];
  const px = doc.settings.parallax;
  const pinst = px.prefab ? instantiatePrefab(px.prefab, px.mods) : null;
  const name = entityNamer(doc);
  const counts = useMemo(() => {
    const c = new Map<string, number>();
    for (const o of [...doc.objects, ...doc.bodies.flatMap((b) => b.objects)]) {
      const n = o.prefab ? prefabLabel(o.prefab) : 'Custom';
      c.set(n, (c.get(n) ?? 0) + 1);
    }
    return [...c.entries()].sort((a, b) => b[1] - a[1]);
  }, [doc, gd]);
  return (
    <div className="panel-scroll">
      <Section title="Level">
        <Row label="Name"><TextInput value={doc.name} onChange={(v) => s.change('Rename level', (d) => { d.name = v; })} /></Row>
        <Row label="Id"><span className="muted">{doc.id}</span></Row>
        <Row label="Background"><ColorInput value={doc.settings.backgroundColor} onChange={(c) => s.change('Background colour', (d) => { d.settings.backgroundColor = c; })} /></Row>
        {doc.source?.unityScene && <Row label="Imported from"><span className="muted small">{doc.source.unityScene}</span></Row>}
      </Section>
      <PlayerSection />
      <PlayerStringSection doc={doc} />
      <Section title="Parallax background">
        <Row label="Prefab">
          <Select value={px.prefab ?? ''} options={parallaxOptions} onChange={(v) => s.change('Parallax', (d) => { d.settings.parallax = { prefab: v || null, mods: [], x: d.settings.parallax.x, y: d.settings.parallax.y }; })} />
        </Row>
        {px.prefab && (
          <>
            <Row label="Position">
              <span className="vec">
                <NumberInput value={px.x} width={70} onChange={(v) => s.change('Parallax', (d) => { d.settings.parallax.x = v; })} />
                <NumberInput value={px.y} width={70} onChange={(v) => s.change('Parallax', (d) => { d.settings.parallax.y = v; })} />
              </span>
            </Row>
            {pinst?.gos.flatMap((g) => g.comps).filter((c) => c.type === 'ParallaxPanel').map((c) => (
              <Section key={c.id} title={`Panel: ${c.go.name}`} open={false}>
                <FieldEditor data={c.data} entityName={name} onSet={(p, v) => s.change('Parallax panel', (d) => setModIn(d.settings.parallax.mods, c.id, p, v))} />
              </Section>
            ))}
          </>
        )}
      </Section>
      <CameraBoundsSection />
      <Section title="Level systems">
        {(['camera', 'levelScripts', 'necessary'] as const).map((k) => {
          const id = doc.system[k];
          return (
            <Row key={k} label={k === 'camera' ? 'Camera' : k === 'levelScripts' ? 'Level scripts' : 'Necessary comps'}>
              {id ? <button className="btn" onClick={() => s.set({ selection: { kind: 'objects', ids: [id] }, panel: 'inspector' })}>Edit {name(`obj:${id}`)}</button> : <span className="muted">missing</span>}
            </Row>
          );
        })}
      </Section>
      <Section title="Contents" open={false}>
        <div className="muted small">
          {doc.layers.length} layers · {doc.terrains.length} terrain shapes · {doc.bodies.length} platforms & blocks · {doc.objects.length} objects · palette {doc.palette.length}
        </div>
        {counts.map(([n, c]) => (
          <div key={n} className="row small"><span className="row-label">{n}</span><span className="row-value muted">{c}</span></div>
        ))}
      </Section>
    </div>
  );
}
